import api from './api';

// PKCE Helper functions
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(buffer: Uint8Array): string {
  let str = '';
  buffer.forEach((byte) => {
    str += String.fromCharCode(byte);
  });
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Microsoft OAuth configuration - for SPA, token exchange must happen client-side
const MICROSOFT_TOKEN_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/token';

export interface MicrosoftAuthURL {
  authUrl: string;
  state: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

export interface MicrosoftTokenResponse {
  connected: boolean;
  microsoftEmail?: string;
  expiresAt?: string;
}

export interface OneDriveItem {
  id: string;
  name: string;
  size?: number;
  isFolder: boolean;
  webUrl?: string;
  downloadUrl?: string;
  mimeType?: string;
  createdAt?: string;
  modifiedAt?: string;
  path?: string;
}

export interface OneDriveListResponse {
  items: OneDriveItem[];
  nextLink?: string;
}

export interface EmailDraft {
  to: string[];
  subject: string;
  body: string;
  bodyType?: 'HTML' | 'Text';
  attachments?: Array<{
    name: string;
    contentType: string;
    contentBytes: string;
  }>;
}

export interface EmailResponse {
  success: boolean;
  messageId?: string;
}

export interface ComposeUrlResponse {
  mailtoUrl: string;
  outlookWebUrl: string;
  msOutlookProtocol: string;
}

const microsoftService = {
  // Auth
  getAuthUrl: async (): Promise<MicrosoftAuthURL & { codeVerifier: string }> => {
    const response = await api.get('/microsoft/auth-url');
    const data = response.data as MicrosoftAuthURL;

    // Generate PKCE code verifier and challenge
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Append PKCE parameters to the auth URL
    const authUrlWithPkce = `${data.authUrl}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

    return {
      authUrl: authUrlWithPkce,
      state: data.state,
      clientId: data.clientId,
      redirectUri: data.redirectUri,
      scope: data.scope,
      codeVerifier,
    };
  },

  // Exchange token directly with Microsoft (SPA flow - must be cross-origin from browser)
  exchangeTokenWithMicrosoft: async (
    code: string,
    codeVerifier: string,
    clientId: string,
    redirectUri: string,
    scope: string
  ): Promise<{
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    scope: string;
  }> => {
    const params = new URLSearchParams();
    params.append('client_id', clientId);
    params.append('code', code);
    params.append('redirect_uri', redirectUri);
    params.append('grant_type', 'authorization_code');
    params.append('code_verifier', codeVerifier);
    params.append('scope', scope);

    const response = await fetch(MICROSOFT_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || 'Token exchange failed');
    }

    return response.json();
  },

  // Store tokens in backend after client-side exchange
  storeTokens: async (tokens: {
    accessToken: string;
    refreshToken?: string;
    expiresIn: number;
    scope: string;
  }): Promise<MicrosoftTokenResponse> => {
    const response = await api.post('/microsoft/store-tokens', tokens);
    return response.data;
  },

  getConnectionStatus: async (): Promise<MicrosoftTokenResponse> => {
    const response = await api.get('/microsoft/status');
    return response.data;
  },

  disconnect: async (): Promise<void> => {
    await api.delete('/microsoft/disconnect');
  },

  // OneDrive
  listFiles: async (folderId?: string): Promise<OneDriveListResponse> => {
    const params = folderId ? { folder_id: folderId } : {};
    const response = await api.get('/microsoft/onedrive/files', { params });
    return response.data;
  },

  getFileInfo: async (fileId: string): Promise<OneDriveItem> => {
    const response = await api.get(`/microsoft/onedrive/file/${fileId}`);
    return response.data;
  },

  getDownloadUrl: async (fileId: string): Promise<{ downloadUrl: string; webUrl: string }> => {
    const response = await api.get(`/microsoft/onedrive/file/${fileId}/download-url`);
    return response.data;
  },

  searchFiles: async (query: string): Promise<OneDriveListResponse> => {
    const response = await api.get('/microsoft/onedrive/search', { params: { query } });
    return response.data;
  },

  // Outlook
  sendEmail: async (email: EmailDraft): Promise<EmailResponse> => {
    const response = await api.post('/microsoft/outlook/send', email);
    return response.data;
  },

  createDraft: async (email: EmailDraft): Promise<EmailResponse> => {
    const response = await api.post('/microsoft/outlook/draft', email);
    return response.data;
  },

  getComposeUrl: async (to?: string, subject?: string, body?: string): Promise<ComposeUrlResponse> => {
    const params: Record<string, string> = {};
    if (to) params.to = to;
    if (subject) params.subject = subject;
    if (body) params.body = body;
    const response = await api.get('/microsoft/outlook/compose-url', { params });
    return response.data;
  },

  // Helper to open file in desktop app or web
  openFile: async (fileId: string, preferDesktop = true): Promise<void> => {
    const { downloadUrl, webUrl } = await microsoftService.getDownloadUrl(fileId);

    if (preferDesktop && downloadUrl) {
      // Try to open with desktop app via download URL
      window.open(downloadUrl, '_blank');
    } else if (webUrl) {
      // Open in Office Online
      window.open(webUrl, '_blank');
    }
  },

  // Helper to open Outlook compose
  openOutlookCompose: async (
    to?: string,
    subject?: string,
    body?: string,
    preferDesktop = true
  ): Promise<void> => {
    const urls = await microsoftService.getComposeUrl(to, subject, body);

    if (preferDesktop) {
      // Try MS Outlook protocol first (opens desktop app)
      window.location.href = urls.msOutlookProtocol;
    } else {
      // Open Outlook Web
      window.open(urls.outlookWebUrl, '_blank');
    }
  },

  // Quick email with mailto (no auth needed)
  openMailto: (to?: string, subject?: string, body?: string): void => {
    const params = new URLSearchParams();
    if (to) params.set('to', to);
    if (subject) params.set('subject', subject);
    if (body) params.set('body', body);
    const mailtoUrl = `mailto:?${params.toString()}`;
    window.location.href = mailtoUrl;
  },

  // Open Excel file by protocol (no auth needed if file is local or shared)
  openExcelProtocol: (fileUrl: string): void => {
    // ms-excel:ofe|u|{url} opens Excel with the file
    // Use different formats to maximize compatibility
    const protocolUrl = `ms-excel:ofe|u|${fileUrl}`;

    // Try using an anchor element which works better in some browsers
    const link = document.createElement('a');
    link.href = protocolUrl;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  // Open PowerPoint file by protocol
  openPowerPointProtocol: (fileUrl: string): void => {
    window.location.href = `ms-powerpoint:ofe|u|${encodeURIComponent(fileUrl)}`;
  },

  // Open Word file by protocol
  openWordProtocol: (fileUrl: string): void => {
    window.location.href = `ms-word:ofe|u|${encodeURIComponent(fileUrl)}`;
  },
};

export { microsoftService };
export default microsoftService;
